import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiAward, FiClock, FiUsers, FiDollarSign, FiArrowLeft, FiCheck, FiAlertCircle, FiBarChart2, FiTrendingUp } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import StatusIndicator from '../components/common/StatusIndicator';
import ReputationBadge from '../components/competition/ReputationBadge';
import EquityCurveComparison from '../components/competition/EquityCurveComparison';

interface Competition {
  id: number;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  submission_deadline: string | null;
  evaluation_type: string;
  prize_pool: number | null;
  entry_cost_credits: number;
  min_reputation_tier: string | null;
  judging_weights: Record<string, number>;
  is_active: boolean;
  is_featured: boolean;
  entry_count: number;
  status: 'upcoming' | 'active' | 'closed';
}

interface LeaderboardEntry {
  rank: number;
  user_id: number;
  user_email: string;
  strategy_name: string;
  deflated_sharpe: number | null;
  raw_return: number | null;
  feature_neutral_return: number | null;
  correlation_score: number | null;
  composite_score: number | null;
  tier: string;
  /** Daily returns for equity curve comparison */
  returns?: number[];
}

interface Strategy {
  id: number;
  name: string;
  backtest_count: number;
}

export default function CompetitionDetailPage() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [myStrategies, setMyStrategies] = useState<Strategy[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [sortBy, setSortBy] = useState('composite_score');
  const [showEquityCurves, setShowEquityCurves] = useState(false);

  useEffect(() => {
    if (competitionId) {
      fetchCompetition();
      fetchLeaderboard();
      fetchMyStrategies();
    }
  }, [competitionId, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCompetition = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/competitions/${competitionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCompetition(await response.json());
      }
    } catch (err) {
      console.error('Error fetching competition:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${getApiUrl()}/api/v1/competitions/${competitionId}/leaderboard?sort_by=${sortBy}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setLeaderboard(await response.json());
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  };

  const fetchMyStrategies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to strategies with at least one backtest
        setMyStrategies(data.filter((s: Strategy) => s.backtest_count > 0));
      }
    } catch (err) {
      console.error('Error fetching strategies:', err);
    }
  };

  const handleSubmitEntry = async () => {
    if (!selectedStrategy) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/competitions/${competitionId}/entries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ strategy_id: selectedStrategy })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        fetchLeaderboard();
      } else {
        const error = await response.json();
        setSubmitError(error.detail || 'Failed to submit entry');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeRemaining = (endDate: string): { text: string; urgent: boolean } => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return { text: 'Ended', urgent: false };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return { text: `${days}d ${hours}h`, urgent: days < 2 };
    if (hours > 0) return { text: `${hours}h ${minutes}m`, urgent: true };
    return { text: `${minutes}m`, urgent: true };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-gray-400">Competition not found</p>
      </div>
    );
  }

  const timeRemaining = getTimeRemaining(competition.end_date);

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/dashboard/competitions')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back to Competitions
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white">{competition.name}</h1>
              <StatusIndicator
                status={competition.status === 'active' ? 'ready' : competition.status === 'upcoming' ? 'busy' : 'idle'}
                label={competition.status.charAt(0).toUpperCase() + competition.status.slice(1)}
                showLabel
              />
            </div>
            {competition.description && (
              <p className="text-gray-400 max-w-2xl">{competition.description}</p>
            )}
          </div>
          {competition.status === 'active' && (
            <div className={`text-right ${timeRemaining.urgent ? 'text-loss' : 'text-gray-400'}`}>
              <p className="text-sm">Time Remaining</p>
              <p className="text-2xl font-bold tabular-nums">{timeRemaining.text}</p>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {competition.prize_pool && (
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <FiDollarSign className="w-4 h-4" />
                <span className="text-sm">Prize Pool</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400 tabular-nums">
                ${competition.prize_pool.toLocaleString()}
              </p>
            </div>
          )}
          <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <FiUsers className="w-4 h-4" />
              <span className="text-sm">Entries</span>
            </div>
            <p className="text-2xl font-bold text-white tabular-nums">{competition.entry_count}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <FiClock className="w-4 h-4" />
              <span className="text-sm">Entry Cost</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {competition.entry_cost_credits > 0 ? `${competition.entry_cost_credits} credits` : 'Free'}
            </p>
          </div>
          {competition.min_reputation_tier && (
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <FiAward className="w-4 h-4" />
                <span className="text-sm">Min Tier</span>
              </div>
              <ReputationBadge tier={competition.min_reputation_tier} size="lg" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Leaderboard */}
          <div className="lg:col-span-2">
            {/* Equity Curve Comparison */}
            {leaderboard.length >= 2 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-medium text-white flex items-center gap-2">
                    <FiTrendingUp className="w-5 h-5 text-profit" />
                    Performance Comparison
                  </h2>
                  <button
                    onClick={() => setShowEquityCurves(!showEquityCurves)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      showEquityCurves
                        ? 'bg-profit/20 text-profit'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                  >
                    {showEquityCurves ? 'Hide Chart' : 'Show Chart'}
                  </button>
                </div>
                {showEquityCurves && (
                  <EquityCurveComparison
                    entries={leaderboard
                      .filter((entry): entry is LeaderboardEntry & { returns: number[] } =>
                        entry.returns !== undefined && entry.returns.length > 0
                      )
                      .slice(0, 5)
                      .map((entry) => ({
                        name: entry.user_email.split('@')[0],
                        returns: entry.returns,
                        rank: entry.rank,
                      }))}
                    startDate={competition.start_date}
                    height={280}
                  />
                )}
                {showEquityCurves && leaderboard.every(e => !e.returns) && (
                  <div className="bg-gray-900 border border-gray-800 rounded-md p-6 text-center">
                    <p className="text-gray-500 text-sm">
                      Equity curve data will be available after entries are scored
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <h2 className="font-medium text-white flex items-center gap-2">
                  <FiBarChart2 className="w-5 h-5" />
                  Leaderboard
                </h2>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-2 py-1"
                >
                  <option value="composite_score">Composite Score</option>
                  <option value="deflated_sharpe">Deflated Sharpe</option>
                  <option value="raw_return">Raw Return</option>
                  <option value="feature_neutral_return">Neutral Return</option>
                  <option value="correlation_score">Originality</option>
                </select>
              </div>

              {leaderboard.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  No entries yet. Be the first to submit!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/50">
                      <tr>
                        <th className="text-left py-2 px-4 text-gray-400 font-medium">Rank</th>
                        <th className="text-left py-2 px-4 text-gray-400 font-medium">Trader</th>
                        <th className="text-left py-2 px-4 text-gray-400 font-medium">Strategy</th>
                        <th className="text-right py-2 px-4 text-gray-400 font-medium">DSR</th>
                        <th className="text-right py-2 px-4 text-gray-400 font-medium">Return</th>
                        <th className="text-right py-2 px-4 text-gray-400 font-medium">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((entry) => (
                        <tr key={`${entry.user_id}-${entry.rank}`} className="border-t border-gray-800 hover:bg-gray-800/30">
                          <td className="py-3 px-4">
                            <span className={`font-bold tabular-nums ${
                              entry.rank === 1 ? 'text-yellow-400' :
                              entry.rank === 2 ? 'text-gray-300' :
                              entry.rank === 3 ? 'text-amber-600' :
                              'text-gray-500'
                            }`}>
                              #{entry.rank}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <ReputationBadge tier={entry.tier} size="sm" />
                              <span className="text-white">{entry.user_email.split('@')[0]}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">{entry.strategy_name}</td>
                          <td className="py-3 px-4 text-right tabular-nums">
                            <span className={entry.deflated_sharpe && entry.deflated_sharpe > 1 ? 'text-profit' : 'text-gray-400'}>
                              {entry.deflated_sharpe?.toFixed(2) ?? '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right tabular-nums">
                            <span className={entry.raw_return && entry.raw_return > 0 ? 'text-profit' : 'text-loss'}>
                              {entry.raw_return ? `${(entry.raw_return * 100).toFixed(1)}%` : '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right tabular-nums text-white font-medium">
                            {entry.composite_score?.toFixed(2) ?? '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Judging Criteria */}
            <div className="mt-6 bg-gray-900 border border-gray-800 rounded-md p-4">
              <h3 className="font-medium text-white mb-3">Judging Criteria</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(competition.judging_weights).map(([key, weight]) => (
                  <div key={key} className="text-center">
                    <p className="text-2xl font-bold text-white tabular-nums">{Math.round(weight * 100)}%</p>
                    <p className="text-xs text-gray-400 capitalize">{key.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Entry Panel */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4 sticky top-8">
              <h3 className="font-medium text-white mb-4">Submit Entry</h3>

              {submitSuccess ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-profit/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiCheck className="w-6 h-6 text-profit" />
                  </div>
                  <p className="text-white font-medium">Entry Submitted!</p>
                  <p className="text-sm text-gray-400 mt-1">Your strategy is now in the competition</p>
                </div>
              ) : competition.status !== 'active' ? (
                <div className="text-center py-6 text-gray-400">
                  <p>Competition is {competition.status === 'upcoming' ? 'not yet started' : 'closed'}</p>
                </div>
              ) : myStrategies.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-400 mb-4">You need a strategy with at least one backtest</p>
                  <button
                    onClick={() => navigate('/dashboard/strategies')}
                    className="px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
                  >
                    Create Strategy
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Select Strategy</label>
                    <select
                      value={selectedStrategy || ''}
                      onChange={(e) => setSelectedStrategy(Number(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                    >
                      <option value="">Choose a strategy...</option>
                      {myStrategies.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.backtest_count} backtests)
                        </option>
                      ))}
                    </select>
                  </div>

                  {competition.entry_cost_credits > 0 && (
                    <div className="mb-4 p-3 bg-gray-800 rounded-md">
                      <p className="text-sm text-gray-400">Entry Cost</p>
                      <p className="text-lg font-medium text-white">{competition.entry_cost_credits} credits</p>
                    </div>
                  )}

                  {submitError && (
                    <div className="mb-4 p-3 bg-red-900/20 border border-red-800/50 rounded-md flex items-start gap-2">
                      <FiAlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
                      <p className="text-sm text-red-400">{submitError}</p>
                    </div>
                  )}

                  <button
                    onClick={handleSubmitEntry}
                    disabled={!selectedStrategy || submitting}
                    className="w-full px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FiAward className="w-4 h-4" />
                        Submit Entry
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
